import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DropdownStatesList from './DropdownStatesList';
import { STATES_ARRAY, UI_TYPE, ZIP_CODE_REGEXP } from '../constants';
import Button from '../../../generic-components/Button';
import Icon from '../../../generic-components/Icon';
import saveButtonNotePen from '../../../assets/svg/saveButtonNotePen.svg';
import Spinner, { SpinnerColor } from '../../../generic-components/Spinner';
import { IAddressObject, IAddressValidationObject } from '../../../types';

interface IProps {
  isLoading: boolean;
  toggler: () => void;
  addressAreaType: string;
  stateObject: { addressObject: IAddressObject; setAddressObject: (object: IAddressObject) => void };
  validationStateObject: {
    validationObject: IAddressValidationObject;
    setValidationObject: (object: IAddressValidationObject) => void;
  };
}

const AddressArea = ({ toggler, addressAreaType, stateObject, isLoading, validationStateObject }: IProps) => {
  const { FORM, TEXT_AREA } = UI_TYPE;

  const { addressObject, setAddressObject } = stateObject;
  const { validationObject, setValidationObject } = validationStateObject;

  const getInitialCurrentStateNumber = () => {
    let res = -1;
    if (addressObject.state) {
      STATES_ARRAY.forEach((state, index) => {
        if (state.POSTAL === addressObject.state) res = index;
      });
    }
    return res;
  };

  const [currentStateNumber, setCurrentStateNumber] = useState<number>(getInitialCurrentStateNumber());
  const isAddressObjectValid = () =>
    validationObject.street_address && validationObject.state && validationObject.city && validationObject.zip_code;

  const handleOnChangeStreet = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const trimmedValue = value.trim();
    if (trimmedValue.length < 31) {
      if (trimmedValue.length > 1 && !validationObject.street_address) {
        setValidationObject({
          ...validationObject,
          street_address: true,
        });
      }
      setAddressObject({ ...addressObject, street_address: value });
    }
  };

  const handleOnBlurStreet = () => {
    if (addressObject.street_address.length > 1 && addressObject.street_address.length < 31) {
      if (!validationObject.street_address) setValidationObject({ ...validationObject, street_address: true });
    } else if (validationObject.street_address) setValidationObject({ ...validationObject, street_address: false });
  };

  const handleOnChangeAptSuite = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const trimmedValue = value.trim();
    if (trimmedValue.length < 31) {
      if (!validationObject.apt_suite) setValidationObject({ ...validationObject, apt_suite: true });
      setAddressObject({ ...addressObject, apt_suite: value });
    }
  };

  const handleOnChangeState = (state: string, currentState: number) => {
    setCurrentStateNumber(currentState);
    setAddressObject({ ...addressObject, state });
  };

  const handleOnChangeCity = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const trimmedValue = value.trim();
    if (trimmedValue.length < 29) {
      if (trimmedValue.length > 2 && !validationObject.city) {
        setValidationObject({
          ...validationObject,
          city: true,
        });
      }
      setAddressObject({ ...addressObject, city: value });
    }
  };

  const handleOnBlurCity = () => {
    if (addressObject.city.length > 2 && addressObject.city.length < 29) {
      if (!validationObject.city) setValidationObject({ ...validationObject, city: true });
    } else if (validationObject.city) setValidationObject({ ...validationObject, city: false });
  };

  const handleOnChangeZipCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const trimmedValue = value.trim();
    if (ZIP_CODE_REGEXP.test(trimmedValue) && trimmedValue.length < 6) {
      if (trimmedValue.length === 5 && !validationObject.zip_code) {
        setValidationObject({
          ...validationObject,
          zip_code: true,
        });
      }
      setAddressObject({ ...addressObject, zip_code: trimmedValue });
    }
  };

  const handleOnBlurZipCode = () => {
    if (addressObject.zip_code.length !== 5) {
      if (validationObject.zip_code) setValidationObject({ ...validationObject, zip_code: false });
    } else if (!validationObject.zip_code) setValidationObject({ ...validationObject, zip_code: true });
  };

  const { t } = useTranslation();

  return (
    <div
      className={`w-full flex flex-col py-4 ${addressAreaType === FORM ? 'h-[324px] gap-[7px]' : 'h-[146px] justify-between'}  rounded-md`}
    >
      {addressAreaType === TEXT_AREA && (
        <>
          <span className='text-bw08 font-metricMedium text-base'>{t('ORDER_NEW_CARD_ADDRESS')}</span>
          <>
            <span className='text-base font-metric text-bw07' data-testid='ONC_view_address'>
              {`${addressObject.apt_suite ? `${addressObject.street_address}, ${addressObject.apt_suite}` : `${addressObject.street_address}`}`}
            </span>
            <span className='text-base font-metric text-bw07' data-testid='ONC_view_cityStateZip'>
              {`${addressObject.city}, ${addressObject.state}, ${addressObject.zip_code}`}
            </span>
          </>
        </>
      )}
      {addressAreaType === FORM && (
        <div className='w-full flex flex-col'>
          <input
            data-testid='ONC_inputAddress'
            onBlur={handleOnBlurStreet}
            placeholder={t('ORDER_NEW_CARD_PLACEHOLDER_STREET')}
            value={addressObject.street_address}
            onChange={handleOnChangeStreet}
            className='caret-action03 w-full h-[44px] bg-bw02 border border-bw03 focus:border-action03 rounded-md outline-none px-[10px] text-sm font-metric placeholder:text-bw06 text-bw08'
            type='text'
          />
          <span
            data-testid='ONC_inputAddressError'
            className='h-4 mt-[2px] mb-[4px] text-xxs font-metricMedium text-red02'
          >
            {!validationObject.street_address && t('CHANGE_ADDRESS_FORM_ERROR_MESSAGE_ADDRESS')}
          </span>
          <input
            data-testid='ONC_inputApt'
            placeholder={t('ORDER_NEW_CARD_PLACEHOLDER_APT')}
            value={addressObject.apt_suite}
            onChange={handleOnChangeAptSuite}
            className='caret-action03 w-full h-[44px] bg-bw02 border border-bw03 focus:border-action03 rounded-md outline-none px-[10px] text-sm font-metric placeholder:text-bw06 text-bw08'
            type='text'
          />
          <span data-testid='ONC_inputAptError' className='h-4 mt-[2px] mb-[4px] text-xxs font-metricMedium text-red02'>
            {!validationObject.apt_suite && t('CHANGE_ADDRESS_FORM_ERROR_MESSAGE_ADDRESS')}
          </span>
          <input
            data-testid='ONC_inputCity'
            onBlur={handleOnBlurCity}
            placeholder={t('ORDER_NEW_CARD_PLACEHOLDER_CITY')}
            value={addressObject.city}
            onChange={handleOnChangeCity}
            className='caret-action03 w-full h-[44px] bg-bw02 border border-bw03 focus:border-action03 rounded-md outline-none px-[10px] text-sm font-metric placeholder:text-bw06 text-bw08'
            type='text'
          />
          <span
            data-testid='ONC_inputCityError'
            className='h-4 mt-[2px] mb-[4px] text-xxs font-metricMedium text-red02'
          >
            {!validationObject.city && t('CHANGE_ADDRESS_FORM_ERROR_MESSAGE_CITY')}
          </span>
          <div className='flex gap-x-2'>
            <DropdownStatesList
              current={currentStateNumber}
              id='StatesList'
              items={STATES_ARRAY}
              onClick={handleOnChangeState}
            />
            <input
              data-testid='ONC_inputZipCode'
              onBlur={handleOnBlurZipCode}
              placeholder={t('ORDER_NEW_CARD_PLACEHOLDER_ZIPCODE')}
              value={addressObject.zip_code}
              onChange={handleOnChangeZipCode}
              className='caret-action03 grow h-[44px] bg-bw02 border border-bw03 focus:border-action03 rounded-md outline-none px-[10px] text-sm font-metric placeholder:text-bw06 text-bw08'
              type='text'
            />
          </div>
          <div className='w-full flex gap-x-1'>
            <div
              data-testid='ONC_dropdownStateError'
              className='w-[170px] h-4 mt-[2px] text-xxs font-metricMedium text-red02'
            >
              {!validationObject.state && t('CHANGE_ADDRESS_FORM_ERROR_MESSAGE_STATE')}
            </div>
            <div
              data-testid='ONC_inputZipCodeError'
              className='w-[170px] h-4 mt-[2px] text-xxs font-metricMedium text-red02'
            >
              {!validationObject.zip_code && t('CHANGE_ADDRESS_FORM_ERROR_MESSAGE_ZIPCODE')}
            </div>
          </div>
        </div>
      )}
      <Button
        onClick={() => {
          if (addressAreaType === FORM) {
            if (isAddressObjectValid()) toggler();
          } else toggler();
        }}
        disable={!isAddressObjectValid() && addressAreaType === FORM}
        className={`w-[56px] h-[24px] self-end rounded-sm flex justify-center items-center ${isAddressObjectValid() && addressAreaType === FORM && !isLoading ? 'bg-action03 hover:bg-action04' : addressAreaType === TEXT_AREA && !isLoading ? 'bg-action03 hover:bg-action04' : 'bg-bw05'} font-metricMedium text-sm text-bw00`}
        id='doneButton'
        childElement={
          isLoading ? (
            <div className='flex items-center justify-center flex-row-reverse'>
              <Spinner
                containerClassName='w-[18px] h-[18px]'
                spinnerColor={SpinnerColor.Blue}
                height='18px'
                width='18px'
              />
            </div>
          ) : addressAreaType === FORM ? (
            <span>{t('ORDER_NEW_CARD_DONE_BUTTON')}</span>
          ) : (
            <>
              <span>{t('EDIT')}</span>
              <Icon className='w-4 h-4' src={saveButtonNotePen} />
            </>
          )
        }
      />
    </div>
  );
};

export default AddressArea;
